<script setup lang="ts">
import { computed, reactive, ref, unref, watch } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  erpIntegrationApi, type IncorporationPayload, type IncorporationScreen, type RequirementSummary,
} from '@/api/erp-integration';
import { apiErrorCode, apiErrorText } from '@/utils/api-errors';
import { useGroupMembershipPicker } from '@/composables/useGroupMembershipPicker';
import { useMobile } from '@/composables/useMobile';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ErpIncorporationLine from './erp-incorporation-line.vue';
import ErpIncorporationPatientStep from './erp-incorporation-patient-step.vue';
import ErpIncorporationSummary, { type PatientSummary } from './erp-incorporation-summary.vue';
import ErpSalesOrderDialog from './erp-sales-order-dialog.vue';
import { type LineDraft, draftError, initialDraft, toReleased, toRows } from './erp-line-draft';
import { type PatientDraft, hasErrors, patientErrors } from './erp-patient-draft';
import { actionError } from './erp-messages';

// The incorporation screen (ADR 0001) as steps: who the Sales orders go to
// (bringing a patient in), one card per Service line confirmed into a
// Requirement (one per career on a Package line), and what confirming will do.
// The Sales orders as Odoo has them open from any step.

type StepKey = 'patient' | 'lines' | 'summary';

const STEP_TITLES: Record<StepKey, string> = {
  patient: 'Paciente', lines: 'Requerimientos', summary: 'Resumen',
};

const props = defineProps<{ orderId: number }>();
const emit = defineEmits<{ close: []; done: [message: string] }>();

const { isPhone } = useMobile();

const { data: screen, isPending, isError } = useQuery({
  queryKey: ['erpIntegration', 'incorporation', computed(() => props.orderId)],
  queryFn: () => erpIntegrationApi.incorporation(props.orderId),
  gcTime: 0,
});

const incorporating = computed(() => screen.value?.mode === 'incorporate');
const drafts = ref<LineDraft[]>([]);
const patient = ref<PatientDraft>({
  mode: 'new', existingId: null, firstName: '', lastName: '', dni: '', address: '',
  latitude: null, longitude: null, goLiveOn: '',
});
const showOrder = ref(false);
const submitError = ref('');

const {
  showGroupPicker, mustPickGroup, selectedGroupIds, groupPickError, groupPickerHint, groupItems,
  validate: validateGroupPick,
} = useGroupMembershipPicker('new', 'patient');
const groupPicker = computed(() => ({
  show: showGroupPicker, required: unref(mustPickGroup), items: unref(groupItems),
  hint: unref(groupPickerHint), error: unref(groupPickError),
}));

function suggestedPatient(value: IncorporationScreen): number | null {
  return value.patient && !value.patient.linked ? value.patient.id : null;
}

// A patient Kuido may already have is offered first.
function prefill(value: IncorporationScreen) {
  drafts.value = value.lines.map((line) => initialDraft(line, value.patient?.requirements ?? []));
  const suggested = suggestedPatient(value);
  patient.value = {
    ...patient.value, mode: suggested ? 'existing' : 'new', existingId: suggested,
    firstName: value.name.firstName ?? '', lastName: value.name.lastName ?? '',
    dni: value.name.dni ?? '', goLiveOn: value.defaultGoLiveOn,
  };
}

watch(screen, (value) => value && prefill(value), { immediate: true });

// The Requirements of the patient the lines go to: the screen's (linked or
// suggested) or those of another patient the coordinator picked.
const screenPatient = computed(() => {
  const value = screen.value?.patient;
  if (!value) return null;

  return value.linked || patient.value.existingId === value.id ? value : null;
});
const pickedPatientId = computed(() => (
  incorporating.value && patient.value.mode === 'existing' && !screenPatient.value ?
    patient.value.existingId : null
));
const { data: pickedPatient } = useQuery({
  queryKey: ['erpIntegration', 'patient', pickedPatientId],
  queryFn: () => erpIntegrationApi.patient(pickedPatientId.value as number),
  enabled: computed(() => !!pickedPatientId.value),
});

const chosenPatient = computed(() => {
  if (incorporating.value && patient.value.mode === 'new') return null;

  return screenPatient.value ?? pickedPatient.value ?? null;
});
const requirements = computed<RequirementSummary[]>(() => chosenPatient.value?.requirements ?? []);
// A patient incorporated before keeps its go-live date: none is asked for.
const incorporatedSince = computed(() => chosenPatient.value?.goLiveOn ?? null);

// A Requirement of another patient can't be continued: the row goes back to
// its default.
watch(requirements, (current) => {
  const ids = current.map((requirement) => requirement.id);
  drafts.value = drafts.value.map((draft) => ({
    ...draft,
    rows: draft.rows.map((row) => {
      if (row.mode !== 'continue' || ids.includes(row.requirementId as number)) return row;

      return { ...row, mode: row.ownRequirementId ? 'keep' : 'create', requirementId: null };
    }),
  }));
});

const patientStepErrors = computed(() => patientErrors(patient.value));

// Steps: a patient already incorporated has none to choose.
const steps = computed<StepKey[]>(() => (
  incorporating.value ? ['patient', 'lines', 'summary'] : ['lines', 'summary']
));
const current = ref(0);
const reached = ref(0);
const currentKey = computed(() => steps.value[current.value]);
const lastStep = computed(() => current.value === steps.value.length - 1);
// A refusal answers the values that were sent: going back to change them clears it.
watch(current, () => {
  submitError.value = '';
});
const attempted = reactive<Record<StepKey, boolean>>({
  patient: false, lines: false, summary: false,
});

function stepValid(key: StepKey): boolean {
  if (key === 'patient') {
    return !hasErrors(patientStepErrors.value) &&
      (patient.value.mode === 'existing' || validateGroupPick());
  }
  if (key === 'lines') return drafts.value.every((draft) => !draftError(draft));

  return true;
}

function goTo(index: number) {
  if (index <= reached.value) current.value = index;
}

function next() {
  if (!stepValid(currentKey.value)) {
    attempted[currentKey.value] = true;

    return;
  }
  current.value += 1;
  reached.value = Math.max(reached.value, current.value);
}

function newPatientSummary(draft: PatientDraft): PatientSummary {
  return { name: `${draft.firstName.trim()} ${draft.lastName.trim()}`, dni: draft.dni.trim(),
    address: draft.address, representative: screen.value?.customer?.name ?? null,
    goLiveOn: draft.goLiveOn, incorporatedBefore: false, sendsPastDays: false };
}

// A patient Kuido already cares for (Linking) goes live as early as the Sales
// orders' first day, within this month: Kuido has those days.
const linking = computed(() => incorporating.value && patient.value.mode === 'existing' &&
  !incorporatedSince.value);
const goLiveOn = computed(() => (
  linking.value ? screen.value?.defaultLinkGoLiveOn ?? '' : patient.value.goLiveOn
));

// A patient Kuido has: the one chosen, or the one the Sales orders already have.
const knownName = computed(() => chosenPatient.value?.name ?? screen.value?.patient?.name);

function knownPatientSummary(name: string): PatientSummary {
  const since = incorporatedSince.value;

  return { name, dni: null, address: null, representative: null,
    goLiveOn: since ?? goLiveOn.value, incorporatedBefore: !!since,
    sendsPastDays: linking.value && goLiveOn.value < (screen.value?.defaultGoLiveOn ?? '') };
}

const patientSummary = computed(() => {
  if (incorporating.value && patient.value.mode === 'new') return newPatientSummary(patient.value);

  return knownName.value ? knownPatientSummary(knownName.value) : null;
});

// Who the Sales orders go to: a patient Kuido has, or a new one (with its
// main address and groups).
function patientPayload(): Partial<IncorporationPayload> {
  const draft = patient.value;
  if (draft.mode === 'existing') return { patientId: draft.existingId as number };

  const value = { firstName: draft.firstName.trim(), lastName: draft.lastName.trim(),
    dni: draft.dni.trim(), address: draft.address, latitude: draft.latitude,
    longitude: draft.longitude };

  return showGroupPicker ?
    { patient: value, groupIds: selectedGroupIds.value } :
    { patient: value };
}

function payload(): IncorporationPayload {
  const value: IncorporationPayload = {
    orderIds: screen.value?.orders.map((order) => order.id) ?? [],
    rows: drafts.value.flatMap(toRows),
    released: drafts.value.flatMap(toReleased),
    dismissedLineIds: drafts.value.filter((draft) => draft.dismissed).map((draft) => draft.lineId),
  };
  if (!incorporating.value) return value;

  const goLive = incorporatedSince.value ? {} : { goLiveOn: goLiveOn.value };

  return { ...value, ...goLive, ...patientPayload() };
}

const { mutate: confirm, isPending: isConfirming } = useMutation({
  mutationFn: (value: IncorporationPayload) => erpIntegrationApi.incorporate(value),
  // A patient just brought in opens on their profile's Requirements;
  // confirming stays here.
  onSuccess: (created) => {
    if (incorporating.value) {
      window.location.href = `/patients/${created.id}?tab=requirements`;

      return;
    }
    emit('done', '¡Requerimientos confirmados!');
  },
  // What Rails rejected (`invalid`) carries no personal data: shown as is.
  onError: (error) => {
    const code = apiErrorCode(error);
    const fallback = 'Ocurrió un error al confirmar. Intenta nuevamente.';
    submitError.value = code === 'invalid' ? apiErrorText(error, fallback) :
      actionError(code, fallback);
  },
});

function close() {
  if (!isConfirming.value) emit('close');
}

// Every step again before sending: the first one missing something opens.
function submit() {
  submitError.value = '';
  const invalid = steps.value.findIndex((key) => !stepValid(key));
  if (invalid >= 0) {
    attempted[steps.value[invalid]] = true;
    current.value = invalid;

    return;
  }

  confirm(payload());
}
</script>
<template>
  <!-- retain-focus off: Google's address suggestions live outside the dialog. -->
  <responsive-dialog
    :model-value="true"
    :title="incorporating ? 'Incorporar paciente' : 'Confirmar requerimientos'"
    max-width="900"
    scrollable
    persistent
    :retain-focus="false"
    @update:model-value="close"
  >
    <div
      v-if="isPending"
      class="py-6 text-medium-emphasis"
    >
      Cargando el presupuesto...
    </div>
    <v-alert
      v-else-if="isError || !screen"
      type="error"
      variant="tonal"
      density="compact"
    >
      Ocurrió un error al cargar el presupuesto. Intenta nuevamente.
    </v-alert>
    <template v-else>
      <div class="d-flex flex-wrap align-center ga-2 mb-2">
        <span
          v-if="!incorporating"
          class="text-body-1"
        >
          Paciente: <strong>{{ screen.patient?.name }}</strong>
        </span>
        <span
          v-if="isPhone"
          class="text-subtitle-2 text-medium-emphasis"
        >
          Paso {{ current + 1 }} de {{ steps.length }} · {{ STEP_TITLES[currentKey] }}
        </span>
        <v-spacer />
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-file-document-outline"
          @click="showOrder = true"
        >
          Ver presupuesto
        </v-btn>
      </div>
      <v-stepper
        :model-value="current + 1"
        flat
        class="incorporation-stepper"
      >
        <!-- Hidden, not removed, on phones: the window follows its items. -->
        <v-stepper-header v-show="!isPhone">
          <template
            v-for="(key, index) in steps"
            :key="key"
          >
            <v-divider v-if="index > 0" />
            <v-stepper-item
              :value="index + 1"
              :title="STEP_TITLES[key]"
              :complete="index < current"
              :editable="index <= reached && index !== current"
              :color="index <= current ? 'primary' : undefined"
              @click="goTo(index)"
            />
          </template>
        </v-stepper-header>
        <v-stepper-window>
          <v-stepper-window-item
            v-for="(key, index) in steps"
            :key="key"
            :value="index + 1"
          >
            <div class="pt-4">
              <erp-incorporation-patient-step
                v-if="key === 'patient'"
                v-model:patient="patient"
                v-model:group-ids="selectedGroupIds"
                :from-notes="screen.name.fromNotes"
                :incorporated-since="incorporatedSince"
                :errors="attempted.patient ? patientStepErrors : {}"
                :group-picker="groupPicker"
              />
              <template v-else-if="key === 'lines'">
                <erp-incorporation-line
                  v-for="(line, lineIndex) in screen.lines"
                  :key="line.id"
                  v-model="drafts[lineIndex]"
                  :line="line"
                  :index="lineIndex"
                  :requirements="requirements"
                  :show-errors="attempted.lines"
                />
              </template>
              <erp-incorporation-summary
                v-else
                :patient="patientSummary"
                :patient-editable="incorporating"
                :lines="screen.lines"
                :drafts="drafts"
                :requirements="requirements"
                @edit="(step) => goTo(steps.indexOf(step))"
              />
            </div>
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
      <v-alert
        v-if="submitError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-2"
      >
        {{ submitError }}
      </v-alert>
      <erp-sales-order-dialog
        v-model="showOrder"
        :orders="screen.orders"
        :lines="screen.lines"
      />
    </template>
    <template #actions>
      <v-btn
        v-if="current > 0"
        prepend-icon="mdi-chevron-left"
        :disabled="isConfirming"
        @click="current -= 1"
      >
        Atrás
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!isPhone"
        variant="text"
        :disabled="isConfirming"
        @click="close"
      >
        Cancelar
      </v-btn>
      <v-btn
        v-if="!lastStep"
        color="primary"
        variant="flat"
        append-icon="mdi-chevron-right"
        :disabled="!screen"
        @click="next"
      >
        Siguiente
      </v-btn>
      <v-btn
        v-else
        color="primary"
        variant="flat"
        :loading="isConfirming"
        @click="submit"
      >
        Confirmar
      </v-btn>
    </template>
  </responsive-dialog>
</template>
<style scoped>
.incorporation-stepper :deep(.v-stepper-header) {
  box-shadow: none;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.incorporation-stepper :deep(.v-stepper-window) {
  margin: 0;
}
</style>
